import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Copy, Trash2, CheckCircle2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function PartnerCodesSection() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partner-codes");
      setCodes(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/partner-codes", { note });
      setCreatedCode(data);
      setNote("");
      toast.success("Code généré");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async (code) => {
    try {
      await api.delete(`/admin/partner-codes/${code}`);
      toast.success("Code supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCreatedCode(null);
  };

  const stats = {
    total: codes.length,
    used: codes.filter((c) => c.used).length,
    available: codes.filter((c) => !c.used).length,
  };

  return (
    <section className="mb-8" data-testid="partner-codes-section">
      <div className="bg-white rounded-2xl border border-[#F0E0CC] overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-[#F0E0CC] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-heading font-semibold text-xl text-ink flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-sage" /> Codes partenaires
            </h2>
            <p className="text-sm text-muted_text mt-1">
              Générez un code unique à envoyer par email après validation d'une demande de partenariat.
              {" "}
              <span className="font-medium text-ink">{stats.available} disponible{stats.available > 1 ? "s" : ""}</span>
              {" / "}
              <span>{stats.used} utilisé{stats.used > 1 ? "s" : ""}</span>
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setCreatedCode(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-sage hover:bg-sage-dark text-white" data-testid="generate-code-button">
                <Plus className="w-4 h-4 mr-1.5" /> Générer un code
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading">Nouveau code partenaire</DialogTitle>
                <DialogDescription>
                  {createdCode
                    ? "Code généré avec succès. Copiez-le et envoyez-le par email au partenaire."
                    : "Ajoutez une note (optionnelle) pour identifier le destinataire avant de générer le code."}
                </DialogDescription>
              </DialogHeader>

              {!createdCode ? (
                <div className="space-y-3 py-2">
                  <Label htmlFor="code-note" className="text-ink font-medium">Note (optionnelle)</Label>
                  <Input
                    id="code-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ex : Mairie de Toulouse"
                    className="h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage"
                    data-testid="code-note-input"
                  />
                </div>
              ) : (
                <div className="py-4 space-y-3">
                  <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-4">
                    <div className="text-xs uppercase tracking-wider text-muted_text font-semibold mb-2">
                      Code à transmettre au partenaire
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 font-mono text-xl font-bold text-ink tracking-wider select-all" data-testid="generated-code-value">
                        {createdCode.code}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copy(createdCode.code)}
                        className="rounded-xl border-[#F0E0CC]"
                        data-testid="copy-code-button"
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copier
                      </Button>
                    </div>
                  </div>
                  {createdCode.note && (
                    <p className="text-sm text-muted_text">Note : {createdCode.note}</p>
                  )}
                </div>
              )}

              <DialogFooter>
                {!createdCode ? (
                  <>
                    <Button variant="outline" onClick={closeDialog} className="rounded-xl border-[#F0E0CC]">
                      Annuler
                    </Button>
                    <Button onClick={onGenerate} disabled={generating} className="rounded-xl bg-sage hover:bg-sage-dark text-white" data-testid="confirm-generate-button">
                      {generating ? "Génération…" : "Générer le code"}
                    </Button>
                  </>
                ) : (
                  <Button onClick={closeDialog} className="rounded-xl bg-sage hover:bg-sage-dark text-white">
                    Fermer
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="p-8 text-center text-muted_text">Chargement…</div>
        ) : codes.length === 0 ? (
          <div className="p-8 sm:p-12 text-center" data-testid="empty-codes">
            <KeyRound className="w-10 h-10 mx-auto text-muted_text mb-3" />
            <h3 className="font-heading font-semibold text-ink">Aucun code généré</h3>
            <p className="text-sm text-muted_text mt-1">
              Créez un code unique pour chaque demande de partenariat validée.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#F0E0CC]">
            {codes.map((c) => (
              <li key={c.code} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="code-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono font-semibold text-ink tracking-wider">{c.code}</code>
                    {c.used ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#D1FAE5] text-[#065F46]">
                        <CheckCircle2 className="w-3 h-3" /> Utilisé
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#FED7AA] text-[#9A3412]">
                        Disponible
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted_text mt-1 flex flex-wrap gap-x-3">
                    {c.note && <span>📌 {c.note}</span>}
                    {c.used_email && <span>👤 {c.used_email}</span>}
                    <span>Créé : {new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!c.used && (
                    <Button variant="outline" size="sm" onClick={() => copy(c.code)} className="rounded-xl border-[#F0E0CC]" data-testid="copy-row-button">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {!c.used && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-xl border-[#F0E0CC] text-red-600" data-testid="delete-code-button">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce code ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Le code <code className="font-mono font-semibold">{c.code}</code> sera définitivement supprimé.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(c.code)} className="bg-red-600 hover:bg-red-700">
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
